import type { User } from '@paintscout/api'
import { reformatSpecialChars } from '@paintscout/util/builder'
import { combineContexts, createQuoteContext, createUserContext, template } from '@paintscout/util/templater'
import striptags from 'striptags'
import type { EmailTemplate, OptionsDocument, QuoteDocument } from 'paintscout'
import type { SendQuoteFormValues } from './SendQuoteForm'

/**
 * Builds the default to/from/subject/body/etc for an email template, rendering its template tags
 * against the quote/user context. Always renders the template's raw (English) copy -- the Work
 * Order Language selector in SendQuoteForm translates the rendered subject/body afterward, live,
 * via translateWorkOrderEmailContent, rather than this function baking in a translation.
 */
export function getDefaultEmail({
  user,
  autofillTo,
  quote,
  options,
  emailTemplate
}: {
  user: User
  autofillTo?: boolean
  quote: QuoteDocument
  options: OptionsDocument
  emailTemplate?: EmailTemplate
}): SendQuoteFormValues {
  const { contact, secondaryContact } = quote
  let from = {
    name: '',
    email: ''
  }
  if (options.options.quotes.quoteEmailFromEstimator && quote.owner?.email && quote.owner?.firstName) {
    from = {
      name: `${quote.owner.firstName}${quote.owner.lastName ? ' ' + quote.owner.lastName : ''}`,
      email: quote.owner.email
    }
  } else if (user.name && user.email) {
    from = {
      name: user.name,
      email: user.email
    }
  } else if (user.email) {
    from = {
      name: '',
      email: user.email
    }
  }

  const templateContext = combineContexts(
    createQuoteContext({ quote, options }),
    createUserContext({ user: { ...user, ...user.user_metadata } })
  )

  const to = [
    ...(autofillTo ? [contact?.email, contact?.alternate_email] : []),
    ...(autofillTo ? [secondaryContact?.email, secondaryContact?.alternate_email] : []),
    ...(emailTemplate.additionalRecipients ?? [])
  ].filter((x) => !!x)

  /*
    This is a bit ugly - we don't really want an HTML editor for the subject,
    but we do need to have template tags in it... So the profile where it's edited
    is an HTML editor, but then we wind-up having to strip/convert it back to text.
    That might be unavoidable/easiest.
  */
  const subject = reformatSpecialChars(striptags(template({ context: templateContext, text: emailTemplate.subject })))

  /*
    Slightly different methodology here - strip all non numbers, and then we'll
    just try to validate that there's an appropriate number of digits
   */
  const phoneNumber = (contact?.phone_number ?? '').replace('+1', '').replaceAll(/[^0-9.]*/g, '')

  return {
    to,
    from,
    subject,
    body: template({ context: templateContext, text: emailTemplate.body }),
    sms: {
      enabled: false,
      to: (autofillTo && phoneNumber) || '',
      // Default the editable SMS text to the subject (the historical send behavior). The estimate
      // link is appended automatically at send time, so it isn't part of this editable value.
      message: subject
    },
    attachPdf: emailTemplate.attachPdf,
    signature: emailTemplate.signature
  }
}
